import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert, Autocomplete, Box, Button, Paper, Skeleton, TextField, Typography,
} from '@mui/material';
import { useUser } from '../context/UserContext';
import { BACKEND_URL } from '../utils/constants';
import { colors } from '../theme';
import {
  BubblePoint, BubbleScatter, ChartCard, DataTable, EmptyNote, Histogram,
  MetricMeta, NO_STAGE, StageFilterBar, StageOption,
  buildBins, fmtCapPct, fmtMoic, fmtMoney, fmtPctPoint, formatterFor, groupFor, median,
} from '../components/MetricCharts';

/**
 * Dashboard → "Deal metrics" tab.
 *
 * Four distributions (how does my book spread?) plus one relationship chart
 * (which deals earn their multiple, and at what size?). Every chart reads the
 * LATEST saved version of each active deal — the same rule the pipeline tab
 * uses — so the two tabs can never disagree about a deal's numbers.
 *
 * The stage filter is client-side: the endpoint returns every deal once and
 * filtering re-slices in memory, so switching stages is instant and never
 * flashes a skeleton. One filter row scopes every chart on the tab.
 *
 * Deals missing a given metric are excluded from that chart rather than
 * plotted as zero, and each card states its own n so a thin chart is obviously
 * thin data, not a thin book.
 */

// --------------------------------------------------------------------------- //
// Types — mirror GET /pipeline/metrics
// --------------------------------------------------------------------------- //
interface DealMetric {
  id: string;
  name: string;
  model_type: string | null;
  state: string | null;
  stage: string | null;        // current pipeline stage, null = untagged
  stage_color: string | null;
  created_at: string | null;
  irr: number | null;          // percent, e.g. 18.4
  moic: number | null;         // multiple, e.g. 2.14
  cap_rate: number | null;     // percent, going-in
  deal_size: number | null;    // acquisition price, dollars
  values: Record<string, number>;  // every well-covered metric, parsed
}
interface Coverage {
  total_deals: number;
  with_version: number;
  irr: number;
  moic: number;
  cap_rate: number;
  deal_size: number;
}
interface MetricsPayload {
  deals: DealMetric[];
  stages: { name: string; color: string | null; sort_order: number }[];
  metric_catalog: MetricMeta[];
  coverage: Coverage;
}

// The scatter opens on the pairing that answers "did this deal earn its
// multiple?" — the rest of the catalogue is one dropdown away.
const DEFAULT_X = 'Levered MOIC';
const DEFAULT_Y = 'Levered IRR';
const DEFAULT_SIZE = 'Deal Size (Acquisition Price)';

const StatTile: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
    <Typography className="num" sx={{ fontSize: '1.875rem', fontWeight: 400, color: colors.navy, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

const DashboardMetrics: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();

  const [data, setData] = useState<MetricsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<string[]>([]);   // [] = no filter
  const [axes, setAxes] = useState<{ x: string; y: string; size: string } | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const r = await fetch(`${BACKEND_URL}/api/pipeline/metrics`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      setData(await r.json());
    } catch (e: any) {
      setError(e?.message || 'Could not load deal metrics.');
    } finally {
      setLoading(false);
    }
  }, [user, getAccessTokenSilently]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const allDeals = useMemo(() => data?.deals ?? [], [data]);
  const cov = data?.coverage;

  // Counts are over ALL deals, not the current slice — a chip has to keep
  // showing what it would select, otherwise every unselected chip reads 0.
  const stageCounts = useMemo(() => {
    const c: Record<string, number> = {};
    allDeals.forEach((d) => {
      const key = d.stage ?? NO_STAGE;
      c[key] = (c[key] ?? 0) + 1;
    });
    return c;
  }, [allDeals]);

  const stageOptions: StageOption[] = useMemo(
    () => (data?.stages ?? []).map((s) => ({ name: s.name, color: s.color })),
    [data],
  );

  const deals = useMemo(
    () => (stageFilter.length === 0
      ? allDeals
      : allDeals.filter((d) => stageFilter.includes(d.stage ?? NO_STAGE))),
    [allDeals, stageFilter],
  );

  const series = useMemo(() => {
    const pick = (key: keyof DealMetric) =>
      deals.map((d) => d[key]).filter((v): v is number => typeof v === 'number');
    return {
      moic: pick('moic'),
      irr: pick('irr'),
      cap: pick('cap_rate'),
      size: pick('deal_size'),
    };
  }, [deals]);

  const bins = useMemo(() => ({
    moic: buildBins(series.moic, 7),
    irr: buildBins(series.irr, 8),
    cap: buildBins(series.cap, 7),
    size: buildBins(series.size, 8),
  }), [series]);

  // ---- configurable scatter ---------------------------------------------- //
  const catalog = useMemo(() => data?.metric_catalog ?? [], [data]);
  const catalogKeys = useMemo(() => catalog.map((c) => c.key), [catalog]);
  const sizeKeys = useMemo(
    () => catalog.filter((c) => c.non_negative).map((c) => c.key),
    [catalog],
  );

  // Resolve defaults once the catalogue arrives; fall back to whatever exists
  // rather than rendering an axis for a metric this account doesn't capture.
  useEffect(() => {
    if (axes || catalogKeys.length === 0) return;
    const pick = (want: string, pool: string[]) =>
      (pool.indexOf(want) >= 0 ? want : pool[0]);
    setAxes({
      x: pick(DEFAULT_X, catalogKeys),
      y: pick(DEFAULT_Y, catalogKeys),
      size: pick(DEFAULT_SIZE, sizeKeys.length ? sizeKeys : catalogKeys),
    });
  }, [axes, catalogKeys, sizeKeys]);

  const unitOf = useCallback(
    (key: string) => catalog.find((c) => c.key === key)?.unit ?? '',
    [catalog],
  );

  const bubbles: BubblePoint[] = useMemo(() => {
    if (!axes) return [];
    return deals
      .filter((d) => typeof d.values?.[axes.x] === 'number' && typeof d.values?.[axes.y] === 'number')
      .map((d) => ({
        id: d.id,
        label: d.name,
        x: d.values[axes.x],
        y: d.values[axes.y],
        size: typeof d.values?.[axes.size] === 'number' ? d.values[axes.size] : null,
        sub: [d.stage, d.model_type, d.state].filter(Boolean).join(' · ') || undefined,
      }));
  }, [deals, axes]);

  const binTable = (b: typeof bins.moic, format: (n: number) => string) => {
    const total = b.reduce((a, x) => a + x.count, 0) || 1;
    return (
      <DataTable
        head={['Range', 'Deals', 'Share']}
        rows={b.map((x) => [
          x.overflow ? `${format(x.x0)} and above` : `${format(x.x0)} – ${format(x.x1)}`,
          x.count,
          `${Math.round((x.count / total) * 100)}%`,
        ])}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Skeleton variant="rounded" height={64} />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={104} />)}
        </Box>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={340} />)}
        </Box>
        <Skeleton variant="rounded" height={480} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchMetrics}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  if (!cov || cov.with_version === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>No saved model results yet</Typography>
        <Typography variant="body2" color="text.secondary">
          Returns appear here once a model has been run and saved — the numbers come from each deal's latest version.
        </Typography>
      </Paper>
    );
  }

  const medIrr = median(series.irr);
  const medMoic = median(series.moic);
  const medCap = median(series.cap);
  const totalVolume = series.size.reduce((a, b) => a + b, 0);
  const n = deals.length;
  const filtered = stageFilter.length > 0;
  const coverageHint = (have: number) => `${have} of ${n} deal${n === 1 ? '' : 's'}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* One filter row, above everything it scopes */}
      {(stageOptions.length > 0 || (stageCounts[NO_STAGE] ?? 0) > 0) && (
        <StageFilterBar
          stages={stageOptions}
          counts={stageCounts}
          selected={stageFilter}
          onChange={setStageFilter}
          shown={n}
          total={allDeals.length}
        />
      )}

      {/* Headline medians — the four numbers you'd quote in a partner meeting */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <StatTile
          label="Median levered IRR"
          value={medIrr == null ? '—' : fmtPctPoint(medIrr)}
          hint={coverageHint(series.irr.length)}
        />
        <StatTile
          label="Median levered MOIC"
          value={medMoic == null ? '—' : fmtMoic(medMoic)}
          hint={coverageHint(series.moic.length)}
        />
        <StatTile
          label="Median going-in cap"
          value={medCap == null ? '—' : fmtCapPct(medCap)}
          hint={coverageHint(series.cap.length)}
        />
        <StatTile
          label="Acquisition volume"
          value={series.size.length ? fmtMoney(totalVolume) : '—'}
          hint={coverageHint(series.size.length)}
        />
      </Box>

      {n === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>No deals in that stage</Typography>
          <Typography variant="body2" color="text.secondary">
            Clear the stage filter, or pick a stage that has deals with saved results.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Distributions */}
          <Box sx={{ display: 'grid', gap: 3, alignItems: 'stretch', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
            <ChartCard
              title="Levered MOIC"
              subtitle="How many deals return each multiple."
              meta={`n = ${series.moic.length}`}
              table={bins.moic.length ? binTable(bins.moic, fmtMoic) : undefined}
            >
              {bins.moic.length ? (
                <Histogram
                  bins={bins.moic}
                  format={fmtMoic}
                  medianValue={medMoic}
                  ariaLabel="Distribution of levered MOIC across deals"
                />
              ) : <EmptyNote text="No saved MOIC in this slice." />}
            </ChartCard>

            <ChartCard
              title="Levered IRR"
              subtitle="How many deals land in each return band."
              meta={`n = ${series.irr.length}`}
              table={bins.irr.length ? binTable(bins.irr, fmtPctPoint) : undefined}
            >
              {bins.irr.length ? (
                <Histogram
                  bins={bins.irr}
                  format={fmtPctPoint}
                  medianValue={medIrr}
                  ariaLabel="Distribution of levered IRR across deals"
                />
              ) : <EmptyNote text="No saved IRR in this slice." />}
            </ChartCard>

            <ChartCard
              title="Going-in cap rate"
              subtitle="Entry pricing across the book."
              meta={`n = ${series.cap.length}`}
              table={bins.cap.length ? binTable(bins.cap, fmtCapPct) : undefined}
            >
              {bins.cap.length ? (
                <Histogram
                  bins={bins.cap}
                  format={fmtCapPct}
                  medianValue={medCap}
                  ariaLabel="Distribution of going-in cap rate across deals"
                />
              ) : <EmptyNote text="No going-in cap rate in this slice." />}
            </ChartCard>

            <ChartCard
              title="Deal size"
              subtitle="Acquisition price per deal."
              meta={`n = ${series.size.length}`}
              table={bins.size.length ? binTable(bins.size, fmtMoney) : undefined}
            >
              {bins.size.length ? (
                <Histogram
                  bins={bins.size}
                  format={fmtMoney}
                  medianValue={median(series.size)}
                  ariaLabel="Distribution of acquisition price across deals"
                />
              ) : <EmptyNote text="No acquisition price in this slice." />}
            </ChartCard>
          </Box>

          {/* Relationship: return vs multiple vs size */}
          <ChartCard
            title="Return profile"
            subtitle={axes
              ? `Every deal placed by ${axes.x} and ${axes.y}; bubble size is ${axes.size}. Top-right of the crosshair beats the median on both.`
              : 'Every deal placed on two metrics of your choosing.'}
            meta={`n = ${bubbles.length}`}
            table={axes && bubbles.length ? (
              <DataTable
                head={['Deal', axes.x, axes.y, axes.size]}
                rows={[...bubbles]
                  .sort((a, b) => b.y - a.y)
                  .map((p) => [
                    p.label,
                    formatterFor(unitOf(axes.x))(p.x),
                    formatterFor(unitOf(axes.y))(p.y),
                    p.size == null ? '—' : formatterFor(unitOf(axes.size))(p.size),
                  ])}
              />
            ) : undefined}
          >
            {axes && (
              <Box
                sx={{
                  display: 'grid', gap: 1.5, mb: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                {([
                  ['Horizontal axis', 'x', catalogKeys],
                  ['Vertical axis', 'y', catalogKeys],
                  ['Bubble size', 'size', sizeKeys.length ? sizeKeys : catalogKeys],
                ] as [string, 'x' | 'y' | 'size', string[]][]).map(([label, slot, options]) => (
                  <Autocomplete
                    key={slot}
                    size="small"
                    options={options}
                    groupBy={groupFor}
                    value={axes[slot]}
                    disableClearable
                    onChange={(_, v) => v && setAxes({ ...axes, [slot]: v })}
                    renderInput={(params) => <TextField {...params} label={label} />}
                  />
                ))}
              </Box>
            )}

            {bubbles.length ? (
              <BubbleScatter
                points={bubbles}
                xLabel={axes?.x}
                yLabel={axes?.y}
                sizeLabel={axes?.size}
                fmtX={formatterFor(unitOf(axes?.x || ''))}
                fmtY={formatterFor(unitOf(axes?.y || ''))}
                fmtSize={formatterFor(unitOf(axes?.size || ''))}
                ariaLabel={`Scatter of ${axes?.x} against ${axes?.y}, sized by ${axes?.size}`}
              />
            ) : (
              <EmptyNote
                text={axes
                  ? `No deal in this slice has both ${axes.x} and ${axes.y} saved.`
                  : 'Needs at least one deal with saved results.'}
                height={280}
              />
            )}
          </ChartCard>
        </>
      )}

      <Typography variant="caption" sx={{ color: colors.grey[600] }}>
        Based on the latest saved version of each active deal.
        {filtered
          ? ` Filtered to ${n} of ${allDeals.length} deals by current stage.`
          : ` ${cov.with_version} of ${cov.total_deals} deals have saved results.`}
        {' '}Deals missing a metric are left out of that chart rather than counted as zero.
      </Typography>
    </Box>
  );
};

export default DashboardMetrics;
